import {useEffect, useRef, useState} from "react";
import {FormattedMessage, useIntl} from "react-intl";
import type {LocationSuggestion} from "@/api/apiClient";
import IconRouteLetterA from "@/assets/icons/icon-route-letter-a.svg?react";
import IconRouteLetterB from "@/assets/icons/icon-route-letter-b.svg?react";
import {Headline} from "@/components/Headline";
import {Overline} from "@/components/Overline";
import {Sheet} from "@/components/Sheet";
import {useConfiguration} from "@/features/configuration";
import {useCurrentLocation} from "@/features/geolocation";
import {
    LocationSuggestions,
    type LocationSuggestionWithCoordinates,
    useEnrichLocation,
} from "@/features/location-suggestions";
import {useOrderContext} from "@/features/order/context/OrderContext";
import {useFlowNavigation} from "@/features/order/hooks/useFlowNavigation";
import {logGtm} from "@/lib/gtm";
import {usePreservedNavigate} from "@/lib/router";
import {SNAP_DISTANCE_IN_METERS} from "../utils/constants";
import styles from "./LocationForm.module.css";
import {LocationInput} from "./LocationInput";

export function LocationForm({type}: {type: "dropoff" | "pickup"}) {
    const pickupRef = useRef<HTMLInputElement | null>(null);
    const dropoffRef = useRef<HTMLInputElement | null>(null);
    const firstSuggestionRef = useRef<{
        suggestion: LocationSuggestion;
        sessionId?: string;
    } | null>(null);
    const intl = useIntl();
    const navigate = usePreservedNavigate();
    const {enrichLocation} = useEnrichLocation();
    const {handleLocationSelected} = useFlowNavigation();
    const {data: configuration} = useConfiguration();
    const {orderDraft, updateOrderDraft} = useOrderContext();
    const {getCurrentLocation, isLoading: isLoadingLocation} = useCurrentLocation({
        snapDistanceInMeters: SNAP_DISTANCE_IN_METERS,
    });

    // Track user-typed query separately from orderDraft-derived query
    const [userTypedQuery, setUserTypedQuery] = useState<string | null>(null);
    const [prevOrderDraft, setPrevOrderDraft] = useState(orderDraft);

    // Reset user typed query during render when orderDraft changes (avoids effect setState)
    if (orderDraft !== prevOrderDraft) {
        setPrevOrderDraft(orderDraft);
        setUserTypedQuery(null);
    }

    // Derive suggestions query: use typed value if user is typing, otherwise use orderDraft
    const suggestionsQuery = userTypedQuery ?? orderDraft[type]?.location?.address ?? "";

    useEffect(() => {
        if (type === "pickup") {
            pickupRef?.current?.focus();
        } else if (type === "dropoff") {
            dropoffRef?.current?.focus();
        }
    }, [type]);

    // Sync input values from orderDraft via refs (DOM update, not React state)
    useEffect(() => {
        const pickupAddress = orderDraft.pickup?.location?.address ?? "";
        const dropoffAddress = orderDraft.dropoff?.location?.address ?? "";

        if (pickupRef.current && pickupRef.current !== document.activeElement) {
            pickupRef.current.value = pickupAddress;
        }
        if (dropoffRef.current && dropoffRef.current !== document.activeElement) {
            dropoffRef.current.value = dropoffAddress;
        }
    }, [orderDraft]);

    const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        e.target.select();
        if (e.target === pickupRef.current) {
            navigate("/taxi/pickup", {replace: true});
        } else if (e.target === dropoffRef.current) {
            navigate("/taxi/dropoff", {replace: true});
        }
    };

    const handleCurrentLocation = async (inputType: "pickup" | "dropoff") => {
        const location = await getCurrentLocation();
        if (!location) {
            return;
        }

        if (inputType === "pickup") {
            logGtm.public.selectPickup("geolocation");

            updateOrderDraft({
                pickup: {
                    location,
                    complete: true,
                    type: "SEARCH",
                },
            });

            handleLocationSelected("pickup");
        } else {
            logGtm.public.selectDropoff("geolocation");

            updateOrderDraft({
                dropoff: {
                    location,
                    complete: true,
                    type: "SEARCH",
                },
            });

            handleLocationSelected("dropoff");
        }
    };

    async function handleSelectLocation(suggestion: LocationSuggestion, suggestSessionId?: string) {
        const locationType = type;
        const hasDropoff = !!orderDraft.dropoff;
        const hasPickup = !!orderDraft.pickup;
        const dropoffElement = dropoffRef.current;
        const pickupElement = pickupRef.current;
        const isPickup = locationType === "pickup";
        const isDropoff = locationType === "dropoff";
        let enrichedLocation;
        try {
            enrichedLocation = await enrichLocation(suggestion.location, suggestSessionId);
        } catch (error) {
            console.error("Failed to enrich location", {
                locationId: suggestion.location.id,
                address: suggestion.location.address,
                error,
            });
            return;
        }

        const enrichedSuggestion: LocationSuggestionWithCoordinates = {
            ...suggestion,
            location: enrichedLocation,
        };

        if (isPickup) {
            updateOrderDraft({pickup: enrichedSuggestion});
            logGtm.public.selectPickup("input");
        } else {
            updateOrderDraft({dropoff: enrichedSuggestion});
            logGtm.public.selectDropoff("input");
        }

        if (isPickup) {
            if (!hasDropoff) {
                if (dropoffElement) {
                    dropoffElement.focus();
                }
            }
        } else if (isDropoff) {
            if (!hasPickup) {
                if (pickupElement) {
                    pickupElement.focus();
                }
            }
        }

        handleLocationSelected(locationType);
    }

    const handleInputChange = (inputType: "pickup" | "dropoff", value: string) => {
        if (inputType === type) {
            setUserTypedQuery(value);
        }
        if (value === "" && orderDraft[inputType]) {
            updateOrderDraft({[inputType]: undefined});
        }
    };

    const handleInputClear = (inputType: "pickup" | "dropoff") => {
        updateOrderDraft({[inputType]: undefined});
    };

    const handleEnterKeyDown = () => {
        const firstSuggestion = firstSuggestionRef.current;
        if (firstSuggestion) {
            handleSelectLocation(firstSuggestion.suggestion, firstSuggestion.sessionId);
        }
    };

    return (
        <Sheet className={styles.sheet}>
            <Sheet.Header>
                <Overline>
                    <FormattedMessage id="order_taxi_without_registration" />
                </Overline>
                <Headline>
                    <FormattedMessage id="enter_route" />
                </Headline>
            </Sheet.Header>
            <Sheet.Body className="pb-0">
                <div className={styles.inputs}>
                    <LocationInput
                        id="pickup"
                        inputRef={pickupRef}
                        defaultValue={orderDraft.pickup?.location?.address ?? ""}
                        onChange={(value) => handleInputChange("pickup", value)}
                        onFocus={handleInputFocus}
                        onEnterKeyDown={handleEnterKeyDown}
                        onCurrentLocation={() => handleCurrentLocation("pickup")}
                        onClear={() => handleInputClear("pickup")}
                        placeholder={intl.formatMessage({id: "location_input_placeholder_pickup"})}
                        disabled={!!configuration?.fixedPickup || isLoadingLocation}
                        startIcon={<IconRouteLetterA />}
                        withCurrentLocationButton={!orderDraft.pickup}
                    />

                    <LocationInput
                        id="dropoff"
                        inputRef={dropoffRef}
                        defaultValue={orderDraft.dropoff?.location?.address ?? ""}
                        onChange={(value) => handleInputChange("dropoff", value)}
                        onFocus={handleInputFocus}
                        onEnterKeyDown={handleEnterKeyDown}
                        onCurrentLocation={() => handleCurrentLocation("dropoff")}
                        onClear={() => handleInputClear("dropoff")}
                        placeholder={intl.formatMessage({id: "location_input_placeholder_dropoff"})}
                        disabled={!!configuration?.fixedDestination || isLoadingLocation}
                        startIcon={<IconRouteLetterB />}
                        withCurrentLocationButton={!orderDraft.dropoff}
                    />
                </div>

                <LocationSuggestions
                    query={suggestionsQuery}
                    type={type}
                    onSelectLocation={handleSelectLocation}
                    queryParams={{
                        near: configuration?.callerIpLocation ?? orderDraft.pickup?.location,
                        pickup: orderDraft.pickup?.location,
                    }}
                    firstSuggestionRef={firstSuggestionRef}
                />
            </Sheet.Body>
        </Sheet>
    );
}
